'use client';

import { useCallback, useEffect, useState } from 'react';

import {
  registerCaptchaTerminalUi,
  registerChallengeUi,
  registerTokenMinter,
} from '@/modules/captcha/core/broker';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { CaptchaChallenge } from './CaptchaChallenge';
import { CaptchaVerificationFailed } from './CaptchaVerificationFailed';

/**
 * Wires the puzzle modal and the reCAPTCHA token minter into the Axios interceptor, and renders
 * the modal when a request needs one.
 *
 * Mount this once, inside `ReCaptchaProvider` — it needs `executeRecaptcha`. Nothing else in the
 * app has to know it exists: the interceptor reaches it through the broker, so every guarded
 * endpoint is covered without a single call site opting in.
 *
 * ## Invariants:
 * - Re-registers the minter whenever `getToken` changes identity, rather than closing over the
 *   first one. Why: `useRecaptcha` rebuilds `getToken` when `executeRecaptcha` arrives, and the
 *   script loads asynchronously — pinning the first value would leave the interceptor holding a
 *   minter that always resolves undefined, so every replay would go out with no fresh token.
 *   The swap is safe because cleanup and re-register both run inside the same commit, leaving
 *   no window in which nothing is registered.
 * - Holds the settle callback in state, not a ref, so the modal opens. Why: the broker hands it
 *   over from outside React; storing it in a ref would update nothing and the modal would never
 *   render.
 * - The dead-end popup REPLACES the puzzle instead of stacking above it, and dropping the puzzle's
 *   settle callback here is safe. Why: the two are alternatives, not layers — the puzzle behind a
 *   non-dismissible popup would be unreachable. The parked request is not orphaned by the drop
 *   because `showCaptchaTerminal` resolves the broker's in-flight challenge itself, which is the
 *   only place that can: concurrent requests share one puzzle, so the request that spent the last
 *   attempt is not necessarily the one this callback belongs to.
 */
export const CaptchaChallengeProvider = ({ children }: { children: React.ReactNode }) => {
  const { getToken } = useRecaptcha();
  const [settle, setSettle] = useState<((solved: boolean) => void) | null>(null);
  const [verificationFailed, setVerificationFailed] = useState(false);

  useEffect(() => registerTokenMinter(getToken), [getToken]);

  useEffect(() => {
    return registerCaptchaTerminalUi(() => {
      setVerificationFailed(true);
      setSettle(null);
    });
  }, []);

  useEffect(() => {
    return registerChallengeUi((onDone) => {
      // Stored via the functional form — React would otherwise call a bare function argument as
      // an updater and store its return value instead of the callback itself.
      setSettle(() => onDone);
    });
  }, []);

  const handleSettle = useCallback(
    (solved: boolean) => {
      setSettle(null);
      settle?.(solved);
    },
    [settle]
  );

  return (
    <>
      {children}
      <CaptchaChallenge open={settle !== null && !verificationFailed} onSettle={handleSettle} />
      <CaptchaVerificationFailed open={verificationFailed} />
    </>
  );
};

CaptchaChallengeProvider.displayName = 'CaptchaChallengeProvider';
